// Pure geometry for the strip chart. No React, no DOM, no clock of its own.
//
// THE ONE IDEA IN THIS FILE. A column's position belongs to the cell it draws,
// not to the moment it is drawn. `Column.x` is content space — how far into the
// video that slice begins, in pixels — so it is identical on every frame for
// the life of the cell. Everything that moves lives in `offsetPx`, a single
// number the rAF loop writes to one CSS transform.
//
// That split is the whole performance story: T10 renders columns as React
// children, which change roughly once per slice (~4s), while the strip scrolls
// at 60fps without React seeing a single one of those frames.
//
// The window is a fixed span of TIME, not of slices. A quality change mid-play
// alters slice durations; if the axis were counted in slices it would visibly
// stretch at the handover, which would look like an instrument lying about its
// own scale. 36 seconds stays 36 seconds.
import type { CompositeCell, CompositeSummary } from '../ledger/composite.js'

/** Seconds visible across the whole strip: 21.6 behind the playhead, 8.4
 * ahead. A round half-minute, because this number is shown to the viewer. */
export const DEFAULT_WINDOW_SECONDS = 30

/** Where the playhead sits across the width. 0.72 leaves the past dominant —
 * this is a record of what has been checked, and only incidentally a preview
 * of what is about to be. */
export const DEFAULT_PLAYHEAD_RATIO = 0.72

export interface Column {
  /** Content space: `cell.start / windowSeconds * widthPx`, measured from the
   * start of the video. Depends only on the cell. */
  x: number
  width: number
  cell: CompositeCell
  /**
   * True when this slice's bytes have not arrived yet.
   *
   * Such a cell IS drawn — as the empty stub every unchecked cell gets, which
   * claims nothing — because hiding it makes the chart lie by omission: the
   * video plainly continues past the buffer, and a chart that stops dead there
   * reads as "there is nothing more" rather than "nothing has been checked
   * here yet". The flag lets the chart draw it fainter still.
   */
  beyondBuffer: boolean
}

export interface StripGeometry {
  /** The visible window plus one column either side, so a column enters and
   * leaves the frame already mounted rather than popping into existence at
   * the edge. Which columns are present changes with time; their `x` does not. */
  columns: Column[]
  playheadX: number
  /** `playheadX - timeToX(currentTime)`. The only value the rAF loop writes. */
  offsetPx: number
}

export interface StripGeometryOptions {
  summary: CompositeSummary
  currentTime: number
  /** How far the bytes have got. Does not cap the strip — it only marks
   * columns as not-yet-arrived; see `Column.beyondBuffer`. */
  bufferedEnd: number
  widthPx: number
  windowSeconds?: number
  playheadRatio?: number
}

/**
 * Content-space x for any moment in the video, in the same space as
 * `Column.x`. Exported because T10 places the quality-handover hairlines and
 * must not re-derive this: two copies of the mapping would drift the moment
 * either changed.
 */
export function timeToX(time: number, windowSeconds: number, widthPx: number): number {
  return (time / windowSeconds) * widthPx
}

/**
 * Index of the first cell whose `end` is greater than `time`, or `cells.length`
 * when every cell ends before it. Binary search: this runs on every ledger
 * change over a cell list that is thousands long for a feature film.
 */
function firstCellEndingAfter(cells: CompositeCell[], time: number): number {
  let low = 0
  let high = cells.length
  while (low < high) {
    const mid = (low + high) >> 1
    if (cells[mid].end > time) high = mid
    else low = mid + 1
  }
  return low
}

export function stripGeometry(opts: StripGeometryOptions): StripGeometry {
  const {
    summary,
    currentTime,
    bufferedEnd,
    widthPx,
    windowSeconds = DEFAULT_WINDOW_SECONDS,
    playheadRatio = DEFAULT_PLAYHEAD_RATIO,
  } = opts

  const playheadX = widthPx * playheadRatio
  const offsetPx = playheadX - timeToX(currentTime, windowSeconds, widthPx)

  const cells = summary.cells
  if (cells.length === 0) return { columns: [], playheadX, offsetPx }

  const behindSeconds = playheadRatio * windowSeconds
  const aheadSeconds = windowSeconds - behindSeconds

  // The visible span, widened by one slice either side. Widening in TIME
  // rather than by counting cells keeps this correct on a ragged ladder where
  // slice durations differ.
  const widest = widestCellDuration(cells)
  const from = currentTime - behindSeconds - widest
  const to = currentTime + aheadSeconds + widest

  const columns: Column[] = []
  for (let index = firstCellEndingAfter(cells, from); index < cells.length; index++) {
    const cell = cells[index]
    if (cell.start >= to) break
    columns.push({
      x: timeToX(cell.start, windowSeconds, widthPx),
      width: timeToX(cell.end - cell.start, windowSeconds, widthPx),
      cell,
      beyondBuffer: cell.end > bufferedEnd,
    })
  }
  return { columns, playheadX, offsetPx }
}

/** The longest cell in the list, used only to widen the cull margin. Scans
 * once; the list is stable for the life of a video. */
function widestCellDuration(cells: CompositeCell[]): number {
  let widest = 0
  for (const cell of cells) {
    const duration = cell.end - cell.start
    if (duration > widest) widest = duration
  }
  return widest
}
